// La tarjeta de `agregar_productos` — ADR-2026-09-08-agregar-productos-es-una-herramienta.
//
// Suma uno o varios productos al catálogo a partir de fotos. Es una CAPACIDAD del agente,
// no una opción de menú: el usuario adjunta fotos con el "+" (o señala una imagen
// generada) y pide "agregámelo como producto"; la tarjeta NO pide nada más. Las fotos
// salen, en este orden, de:
//   • un lote grande en memoria (`lote_id`);
//   • imágenes generadas en el chat (`image_ids`, bajadas del storage);
//   • las fotos que el usuario adjuntó a su último mensaje con fotos (quedan resumidas en
//     el mensaje, ver `adjuntosDelMensaje.ts`; se vuelven a firmar y se bajan).
//
// Con la misma cocina que la carga masiva de Mi marca: agrupado con IA (hasta 4 fotos por
// producto, nombre sugerido) o un solo producto si el usuario habló en singular, límite de
// productos por carga del plan, y `uploadNewProduct` (producto + variante + fotos full/opt
// + categorización IA). Sin fotos en ninguna fuente, la tarjeta lo dice y se resuelve sola
// con `motivo: "sin_fotos"` para que el agente pida que las adjunten.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BotonGenerarSinPreguntar } from '../components/BotonGenerarSinPreguntar';
import { Loader2, Package, X } from 'lucide-react';
import { agruparImagenesConIa } from '../../../lib/agruparImagenesIa';
import { MAX_IMAGENES_POR_PRODUCTO } from '../../../lib/limiteImagenesProducto';
import { getBulkPlanLimit } from '../../../hooks/useBulkUpload';
import { mapWithConcurrency } from '../../../utils/concurrency';
import {
  normalizeImageForUpload,
  UNSUPPORTED_FORMAT_MESSAGE,
  UnsupportedImageFormatError,
} from '../../../utils/heicConvert';
import { preloadGeneratedImages } from '../../../utils/preloadGeneratedImages';
import { adjuntoPorId, ultimasImagenesAdjuntadas, ultimoLoteAdjuntado } from '../adjuntosDelMensaje';
import { signReferenceImage } from '../agentAttachments';
import { useAgentEnv } from '../agentEnvContext';
import { useProductEdit } from '../productsContext';
import { uploadNewProduct } from '../productUploadApi';
import type { UseV2ChatReturn, V2ToolCard } from '../useV2Chat';
import { liberarLoteDeFotos, obtenerLoteDeFotos } from './lotesDeFotos';
import {
  esUnSoloProducto,
  fuenteDeFotos,
  idDeFoto,
  MAX_FOTOS_LOTE,
  mensajeLimiteProductos,
  nombreParaMostrar,
  nombrePedido,
  propuestaDesdeGrupos,
  propuestaPlana,
  propuestaUnica,
  recortarAlLimite,
  resumenCreacionParaAgente,
  type EntradaAgregarProductos,
  type ProductoCreado,
  type ProductoPropuesto,
  type ResumenCreacion,
} from './organizarProductos';

type Fase =
  | 'buscando'
  | 'sin_fotos'
  | 'preparando'
  | 'agrupando'
  | 'propuesta'
  | 'creando'
  | 'listo';

const ETIQUETA = 'text-[12.5px] uppercase tracking-wide font-medium text-[#3C3C43]/55';
const CONCURRENCIA_CREACION = 3;
const CONCURRENCIA_DESCARGA = 4;

/** Baja una imagen (URL firmada de nuestro storage) como `File`, para subirla como foto
 *  de producto. null si no se pudo: la foto se saltea con aviso. */
async function descargarComoArchivo(url: string, nombre: string): Promise<File | null> {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    if (blob.size === 0) return null;
    return new File([blob], nombre, { type: blob.type || 'image/jpeg' });
  } catch {
    return null;
  }
}

const nombreDePath = (path: string): string => path.split('/').pop() || 'foto.jpg';

export const AgregarProductosCard: React.FC<{
  card: V2ToolCard;
  active: boolean;
  chat: UseV2ChatReturn;
}> = ({ card, active, chat }) => {
  const { userId, companyId, plan, brandId } = useAgentEnv();
  const { refreshProducts } = useProductEdit();
  const input = (card.input ?? {}) as EntradaAgregarProductos;
  const fuente = useMemo(() => fuenteDeFotos(input), [input]);
  const unSolo = esUnSoloProducto(input);
  const nombreDelPedido = nombrePedido(input);
  const mensaje = typeof input.mensaje === 'string' ? input.mensaje.trim() : '';
  // Sin plan resuelto todavía (o un proveedor que no lo pasa) se asume pago: el agente
  // ya está cerrado a las cuentas gratis del lado del backend.
  const limite = getBulkPlanLimit(plan || 'pro');

  const [archivos, setArchivos] = useState<File[]>([]);
  const [fase, setFase] = useState<Fase>('buscando');
  const [aviso, setAviso] = useState<string | null>(null);
  const [motivoSinFotos, setMotivoSinFotos] = useState<string>('');
  const [propuesta, setPropuesta] = useState<ProductoPropuesto[]>([]);
  const [progreso, setProgreso] = useState({ hechos: 0, total: 0 });
  const arrancoRef = useRef(false);
  const resueltoRef = useRef(false);
  const loteIdRef = useRef<string | null>(null);

  // Previews: object URLs de los archivos vigentes, revocadas al cambiar / desmontar.
  const previews = useMemo(() => {
    const m = new Map<string, string>();
    archivos.forEach((f, i) => {
      try {
        m.set(idDeFoto(i), URL.createObjectURL(f));
      } catch {
        /* sin preview */
      }
    });
    return m;
  }, [archivos]);
  useEffect(
    () => () => {
      previews.forEach((u) => {
        try {
          URL.revokeObjectURL(u);
        } catch {
          /* noop */
        }
      });
    },
    [previews],
  );

  // ── Preparar (formatos) + agrupar (IA) o producto único ────────────────
  const procesar = useCallback(
    async (crudos: File[]) => {
      setAviso(null);
      setFase('preparando');
      // Misma política de formatos que toda carga: HEIC/AVIF/WEBP → JPEG; RAW/TIFF rebotan.
      const listos: File[] = [];
      let rechazadas = 0;
      let motivo: string | null = null;
      for (const f of crudos.slice(0, MAX_FOTOS_LOTE)) {
        try {
          listos.push(await normalizeImageForUpload(f));
        } catch (err) {
          rechazadas += 1;
          motivo =
            motivo ??
            // `err.message`: la clase también es la de "el navegador no pudo leer el
            // archivo", que trae su propio texto (ADR-2026-10-24-subida-lee-el-archivo-antes).
            (err instanceof UnsupportedImageFormatError
              ? err.message || UNSUPPORTED_FORMAT_MESSAGE
              : `No pudimos procesar "${f.name}".`);
        }
      }
      if (crudos.length > MAX_FOTOS_LOTE) {
        motivo = `Tomé las primeras ${MAX_FOTOS_LOTE} fotos: es el máximo por tanda.`;
      } else if (rechazadas > 0 && motivo) {
        motivo = `${rechazadas} foto(s) quedaron afuera: ${motivo}`;
      }
      setArchivos(listos);
      if (listos.length === 0) {
        setMotivoSinFotos(motivo ?? 'No quedó ninguna foto para cargar.');
        setFase('sin_fotos');
        return;
      }
      const lista = listos.map((f, i) => ({ id: idDeFoto(i), nombre: f.name }));
      let resultado: ProductoPropuesto[];
      if (unSolo || listos.length < 2) {
        // "Agregámelo" (singular) o una sola foto: un producto, sin pasar por la IA.
        const r = propuestaUnica(lista, nombreDelPedido, MAX_IMAGENES_POR_PRODUCTO);
        resultado = r.propuesta;
        if (r.sobrantes > 0) {
          motivo = `${motivo ? `${motivo} ` : ''}Un producto lleva hasta ${MAX_IMAGENES_POR_PRODUCTO} fotos: ${r.sobrantes} quedaron afuera.`;
        }
      } else {
        setFase('agrupando');
        try {
          const grupos = await agruparImagenesConIa(
            listos.map((file, i) => ({ id: idDeFoto(i), file })),
          );
          resultado = propuestaDesdeGrupos(lista, grupos, MAX_IMAGENES_POR_PRODUCTO);
        } catch (err) {
          // Sin IA cada foto es un producto: el usuario puede unirlas después desde Mi marca.
          resultado = propuestaPlana(lista);
          motivo = `${motivo ? `${motivo} ` : ''}No pude agrupar con IA (${err instanceof Error ? err.message : 'error'}): dejé cada foto como un producto.`;
        }
        // Con un nombre dicho por el usuario y un solo producto resultante, va ese nombre.
        if (nombreDelPedido && resultado.length === 1 && !resultado[0].nombre) {
          resultado = [{ ...resultado[0], nombre: nombreDelPedido }];
        }
      }
      setPropuesta(resultado);
      setAviso(motivo);
      setFase('propuesta');
    },
    [unSolo, nombreDelPedido],
  );

  // ── De dónde salen las fotos ────────────────────────────────────────────
  useEffect(() => {
    if (arrancoRef.current || card.status === 'resolved') return;
    // Las generadas y las adjuntas necesitan la empresa para firmar/bajar; el lote no.
    if (fuente.tipo !== 'lote' && !companyId) return;
    arrancoRef.current = true;
    void (async () => {
      let crudos: File[] = [];
      let motivo = '';
      if (fuente.tipo === 'lote') {
        const lote = obtenerLoteDeFotos(fuente.loteId);
        if (lote) {
          loteIdRef.current = lote.id;
          crudos = lote.archivos;
        } else {
          motivo =
            'Las fotos del lote ya no están (se recargó la página). Volvé a adjuntarlas con el "+".';
        }
      } else if (fuente.tipo === 'generadas') {
        try {
          const imgs = await preloadGeneratedImages(fuente.imageIds, companyId as string);
          const bajadas = await mapWithConcurrency(imgs, CONCURRENCIA_DESCARGA, (img) =>
            descargarComoArchivo(img.signedUrl, img.fileName || `${img.id}.jpg`),
          );
          crudos = bajadas.filter((f): f is File => !!f);
          if (crudos.length < fuente.imageIds.length) {
            motivo = `Bajé ${crudos.length} de ${fuente.imageIds.length} imágenes generadas.`;
          }
        } catch {
          motivo = 'No pude bajar las imágenes generadas. Probá de nuevo.';
        }
      } else if (fuente.tipo === 'porId') {
        const encontradas = fuente.adjuntoIds
          .map((id) => adjuntoPorId(chat.messages, id))
          .filter((a): a is NonNullable<typeof a> => !!a);
        const bajadas = await mapWithConcurrency(encontradas, CONCURRENCIA_DESCARGA, async (a) => {
          const url = await signReferenceImage(a.storagePath, a.bucket);
          return url ? descargarComoArchivo(url, nombreDePath(a.storagePath)) : null;
        });
        crudos = bajadas.filter((f): f is File => !!f);
        if (crudos.length < fuente.adjuntoIds.length) {
          motivo = crudos.length
            ? `Encontré ${crudos.length} de ${fuente.adjuntoIds.length} fotos.`
            : 'No encontré esas fotos en el chat.';
        }
      } else {
        // Las fotos del último mensaje del usuario que las tenga; si en cambio mandó un
        // lote y el agente no pasó su id, el lote.
        const adjuntas = ultimasImagenesAdjuntadas(chat.messages);
        if (adjuntas.length > 0) {
          const bajadas = await mapWithConcurrency(adjuntas, CONCURRENCIA_DESCARGA, async (a) => {
            const url = await signReferenceImage(a.storagePath, a.bucket);
            return url ? descargarComoArchivo(url, nombreDePath(a.storagePath)) : null;
          });
          crudos = bajadas.filter((f): f is File => !!f);
          if (crudos.length === 0) motivo = 'No pude bajar las fotos que adjuntaste. Probá de nuevo.';
        } else {
          const lote = ultimoLoteAdjuntado(chat.messages);
          const enMemoria = lote ? obtenerLoteDeFotos(lote.loteId) : null;
          if (enMemoria) {
            loteIdRef.current = enMemoria.id;
            crudos = enMemoria.archivos;
          } else {
            motivo = 'No encontré fotos. Adjuntalas con el "+" y volvé a pedírmelo.';
          }
        }
      }
      if (crudos.length === 0) {
        setMotivoSinFotos(motivo || 'No encontré fotos. Adjuntalas con el "+" y volvé a pedírmelo.');
        setFase('sin_fotos');
        return;
      }
      await procesar(crudos);
    })();
    // Arranca una vez, cuando la empresa está resuelta.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, fuente.tipo]);

  // Sin fotos la tarjeta se resuelve sola: el agente se entera y le pide al usuario que
  // las adjunte. Sólo cuando es la activa (si no, `resolveToolCard` la ignora).
  useEffect(() => {
    if (fase !== 'sin_fotos' || !active || resueltoRef.current || card.status === 'resolved') return;
    resueltoRef.current = true;
    chat.resolveToolCard(card.id, {
      created: false,
      cantidad: 0,
      productos: [],
      fallidos: [],
      fuera_por_plan: 0,
      motivo: 'sin_fotos',
      detalle: motivoSinFotos,
    });
  }, [fase, active, card.id, card.status, chat, motivoSinFotos]);

  const renombrar = (id: string, nombre: string) =>
    setPropuesta((prev) => prev.map((p) => (p.id === id ? { ...p, nombre } : p)));
  const quitar = (id: string) => setPropuesta((prev) => prev.filter((p) => p.id !== id));

  const { aceptados, sobrantes } = recortarAlLimite(propuesta, limite);
  const avisoPlan = mensajeLimiteProductos(limite, sobrantes.length);

  // ── Crear ───────────────────────────────────────────────────────────────
  const crear = async () => {
    if (!active || fase !== 'propuesta' || aceptados.length === 0) return;
    if (!userId || !companyId) {
      setAviso('No pudimos identificar tu cuenta. Probá recargar.');
      return;
    }
    setFase('creando');
    setProgreso({ hechos: 0, total: aceptados.length });
    const archivoPorId = new Map(archivos.map((f, i) => [idDeFoto(i), f]));
    const creados: Array<ProductoCreado | null> = new Array(aceptados.length).fill(null);
    const fallidos: Array<{ nombre: string; motivo: string } | null> = new Array(
      aceptados.length,
    ).fill(null);
    await mapWithConcurrency(aceptados, CONCURRENCIA_CREACION, async (p, i) => {
      const files = p.fotoIds
        .map((id) => archivoPorId.get(id))
        .filter((f): f is File => !!f)
        .slice(0, MAX_IMAGENES_POR_PRODUCTO);
      try {
        // Nombre vacío ⇒ placeholder + nombre por IA (categorizeProduct completarNombre).
        const r = await uploadNewProduct({
          companyId,
          userId,
          name: p.nombre.trim(),
          files,
          brandId: brandId ?? null,
        });
        creados[i] = { product_id: r.variantId, nombre: r.finalName, fotos: files.length };
      } catch (err) {
        fallidos[i] = {
          nombre: nombreParaMostrar(p, i),
          motivo: err instanceof Error && err.message ? err.message : 'no se pudo crear',
        };
      }
      setProgreso((prev) => ({ ...prev, hechos: prev.hechos + 1 }));
    });
    const resumen = resumenCreacionParaAgente({
      creados: creados.filter((c): c is ProductoCreado => !!c),
      fallidos: fallidos.filter((f): f is { nombre: string; motivo: string } => !!f),
      fueraPorPlan: sobrantes.length,
    });
    liberarLoteDeFotos(loteIdRef.current);
    void refreshProducts();
    setFase('listo');
    resueltoRef.current = true;
    chat.resolveToolCard(card.id, resumen);
  };

  // Auto-crear: con el permiso de catálogo en directo, la propuesta se crea sola apenas
  // está lista (ADR-2026-09-08-permisos-tambien-para-el-catalogo). El guard de `crear`
  // (fase + cuenta) evita el doble disparo; el límite del plan y los fallos por producto
  // siguen valiendo igual, así que el resumen que vuelve al agente es el mismo.
  const autoCrear = (card.input as { autogenerate?: boolean } | null)?.autogenerate === true;
  useEffect(() => {
    // Igual que el lote: una card restaurada (`detached`) no crea sola. Sin esto, cada
    // recarga volvería a crear los mismos productos.
    if (card.status !== 'pending' || card.detached) return;
    if (!autoCrear || !active || fase !== 'propuesta' || aceptados.length === 0) return;
    void crear();
    // `crear` se recrea en cada render; el guard de adentro es el que manda.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoCrear, active, fase, aceptados.length, card.status, card.detached]);

  // ── RESUELTA ────────────────────────────────────────────────────────────
  if (card.status === 'resolved' || fase === 'listo') {
    const r = (card.response ?? {}) as Partial<ResumenCreacion> & { motivo?: string; detalle?: string };
    const productos = r.productos ?? [];
    if (productos.length === 0 && r.motivo === 'sin_fotos') {
      return (
        <div className="p-4">
          <p className={`${ETIQUETA} mb-1`}>Agregar productos</p>
          <p className="text-[14px] leading-snug text-[#02062B]/[0.85]">
            {r.detalle || 'No encontré fotos. Adjuntalas con el "+" y volvé a pedírmelo.'}
          </p>
        </div>
      );
    }
    return (
      <div className="p-4">
        <p className={`${ETIQUETA} mb-2`}>Listo</p>
        <p className="mb-2 text-[14px] leading-snug text-[#02062B]/[0.85]">
          {productos.length === 0
            ? 'No se creó ningún producto.'
            : productos.length === 1
              ? 'Sumamos 1 producto a tu catálogo.'
              : `Sumamos ${productos.length} productos a tu catálogo.`}
        </p>
        {productos.length > 0 && (
          <ul className="mb-2 space-y-1 text-[13px] text-[#02062B]/[0.8]">
            {productos.map((p) => (
              <li key={p.product_id} className="flex items-center gap-2">
                <Package className="h-3.5 w-3.5 shrink-0 text-[#02062B]/[0.45]" />
                <span className="truncate">{p.nombre}</span>
                <span className="shrink-0 text-[12px] text-[#02062B]/[0.45]">
                  {p.fotos} {p.fotos === 1 ? 'foto' : 'fotos'}
                </span>
              </li>
            ))}
          </ul>
        )}
        {(r.fallidos?.length ?? 0) > 0 && (
          <ul className="space-y-1 text-[12.5px] text-[#C33B36]">
            {r.fallidos!.map((f, i) => (
              <li key={`${f.nombre}-${i}`}>
                No se pudo crear {f.nombre}: {f.motivo}
              </li>
            ))}
          </ul>
        )}
        {(r.fuera_por_plan ?? 0) > 0 && (
          <p className="mt-1 text-[12.5px] text-[#02062B]/[0.55]">
            {r.fuera_por_plan} quedaron afuera por el límite de tu plan.
          </p>
        )}
      </div>
    );
  }

  // ── SIN FOTOS (todavía no resuelta: la resuelve el efecto de arriba) ────
  if (fase === 'sin_fotos') {
    return (
      <div className="p-4">
        <p className={`${ETIQUETA} mb-1`}>Agregar productos</p>
        <p className="text-[14px] leading-snug text-[#02062B]/[0.85]">{motivoSinFotos}</p>
      </div>
    );
  }

  // ── BUSCANDO / PREPARANDO / AGRUPANDO / CREANDO ─────────────────────────
  if (fase !== 'propuesta') {
    const texto =
      fase === 'buscando'
        ? 'Buscando las fotos…'
        : fase === 'preparando'
          ? `Preparando ${archivos.length || ''} fotos…`.replace('  ', ' ')
          : fase === 'agrupando'
            ? `Agrupando ${archivos.length} fotos por producto…`
            : `Creando productos… ${progreso.hechos} de ${progreso.total}`;
    return (
      <div className="flex items-center gap-3 p-5">
        <Loader2 className="h-5 w-5 shrink-0 animate-spin text-brand-primary" />
        <p className="text-[14px] text-[#02062B]/[0.85]">{texto}</p>
      </div>
    );
  }

  // ── PROPUESTA ───────────────────────────────────────────────────────────
  return (
    <div className="p-5">
      <p className={`${ETIQUETA} mb-1`}>
        {propuesta.length} {propuesta.length === 1 ? 'producto' : 'productos'} · {archivos.length}{' '}
        {archivos.length === 1 ? 'foto' : 'fotos'}
      </p>
      <p className="mb-3 text-[14px] leading-snug text-[#02062B]/[0.85]">
        {mensaje ||
          (propuesta.length === 1
            ? 'Revisá el nombre y tocá Crear.'
            : 'Revisá cómo quedaron agrupadas y los nombres, y tocá Crear.')}
      </p>

      <ul className="mb-4 divide-y divide-[#02062B]/[0.06]">
        {propuesta.map((p, i) => {
          const afuera = i >= limite;
          return (
            <li key={p.id} className={`py-2.5 ${afuera ? 'opacity-50' : ''}`}>
              <div className="flex items-center gap-3">
                <div className="flex shrink-0 -space-x-2">
                  {p.fotoIds.slice(0, MAX_IMAGENES_POR_PRODUCTO).map((id) => {
                    const src = previews.get(id);
                    return (
                      <span
                        key={id}
                        className="h-12 w-12 overflow-hidden rounded-lg border-2 border-white bg-[#F0F2F7]"
                      >
                        {src ? <img src={src} alt="" className="h-full w-full object-cover" /> : null}
                      </span>
                    );
                  })}
                </div>
                <div className="min-w-0 flex-1">
                  <input
                    type="text"
                    value={p.nombre}
                    onChange={(e) => renombrar(p.id, e.target.value)}
                    placeholder={nombreParaMostrar({ ...p, nombre: '' }, i)}
                    disabled={!active || afuera}
                    aria-label="Nombre del producto"
                    className="w-full rounded-lg border border-transparent bg-transparent px-1.5 py-1 text-[13.5px] font-semibold text-brand-black outline-none placeholder:font-medium placeholder:text-[#02062B]/[0.4] hover:border-[#02062B]/[0.1] focus:border-[#1644EB]/[0.4] focus:bg-white"
                  />
                  <p className="px-1.5 text-[12px] text-[#02062B]/[0.5]">
                    {p.fotoIds.length} {p.fotoIds.length === 1 ? 'foto' : 'fotos'}
                    {afuera ? ' · queda afuera por el plan' : ''}
                  </p>
                </div>
                {propuesta.length > 1 && (
                  <button
                    type="button"
                    aria-label={`Quitar ${nombreParaMostrar(p, i)}`}
                    onClick={() => quitar(p.id)}
                    disabled={!active}
                    className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[#02062B]/[0.45] transition-colors enabled:hover:bg-[#02062B]/[0.08] enabled:hover:text-brand-black disabled:opacity-40"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {(aviso || avisoPlan) && (
        <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-[13px] text-amber-800">
          {[aviso, avisoPlan].filter(Boolean).join(' ')}
        </div>
      )}

      <button
        type="button"
        onClick={crear}
        disabled={!active || aceptados.length === 0}
        className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-brand-primary py-3 px-6 text-[15px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-primary-hover disabled:opacity-50"
      >
        <Package className="h-4 w-4" />
        Crear {aceptados.length} {aceptados.length === 1 ? 'producto' : 'productos'}
      </button>
      {/* Crea y deja el catálogo en directo para las próximas
          (ADR-2026-09-13-generar-sin-preguntarme-en-la-card). Nunca cuando ya está
          creando sola: ahí no hay confirmación que sacar. */}
      {!autoCrear && (
        <BotonGenerarSinPreguntar
          tipo="productos"
          noPreguntarMas={chat.noPreguntarMas}
          onAccion={crear}
          disabled={!active || aceptados.length === 0}
        />
      )}
      <p className="mt-2 text-center text-[12px] text-[#02062B]/[0.5]">
        Sin nombre, la IA se lo pone. No consume créditos.
      </p>
    </div>
  );
};

export default AgregarProductosCard;
